import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CloseIcon from "@mui/icons-material/Close";
import PvComparisonSummary from "./pvGeneration/PvComparisonSummary";
import PvKpiCards from "./pvGeneration/PvKpiCards";
import PvGenerationChart from "./pvGeneration/PvGenerationChart";
import WindRunHistoryTable from "./windGeneration/WindRunHistoryTable";
import {
  buildCsv,
  computeSeriesEnergyMwh,
  downloadBlob,
  formatRunTimestamp,
  seriesMaxDifferenceKw,
  seriesPeakKw,
} from "./pvGeneration/pvGenerationUtils";
import {
  fetchWindGenerationRuns,
  fetchWindGenerationRunSeries,
  triggerWindGenerationRun,
} from "../services/api";
import { formatApiError } from "../utils/apiErrors";

const RUN_TYPE_OPTIONS = [
  { key: "forecast", label: "Forecast weather" },
  { key: "actual_weather", label: "Historical weather" },
];

// Wind runs are fast; poll frequently enough that the transient "running"
// state is visible in the run history instead of jumping queued -> completed.
const POLL_INTERVAL_MS = 2000;

// Mirrors the PV Generation tab structure: comparison summary, KPI cards,
// comparison chart, and a filterable run history table. Wind has no SCADA /
// measured source yet, so accuracy metrics and the measured overlay are
// intentionally absent; exports are generated client-side as CSV.
export default function WindGenerationPanel({
  projectId,
  accessToken,
  timeZone,
  windEnabled,
  runType,
  onRunTypeChange,
  onOpenRangePicker,
  rangeLabelNode,
  triggerRange,
}) {
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState("");
  const [triggering, setTriggering] = useState(false);
  const [triggerError, setTriggerError] = useState("");
  const [selectedRunIds, setSelectedRunIds] = useState([]);
  const [seriesByRun, setSeriesByRun] = useState({});
  const [seriesLoadingIds, setSeriesLoadingIds] = useState([]);
  const autoTrackRef = useRef(true);
  const historyRef = useRef(null);
  const [chartFullscreen, setChartFullscreen] = useState(false);

  const loadRuns = useCallback(async () => {
    if (!projectId) return;
    setRunsLoading(true);
    setRunsError("");
    try {
      const rows = await fetchWindGenerationRuns({ projectId }, accessToken);
      setRuns(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setRunsError(formatApiError(err, "Failed to fetch wind generation runs."));
    } finally {
      setRunsLoading(false);
    }
  }, [projectId, accessToken]);

  useEffect(() => {
    autoTrackRef.current = true;
    setSelectedRunIds([]);
    setSeriesByRun({});
    loadRuns();
  }, [loadRuns]);

  const pendingCount = useMemo(
    () => runs.filter((r) => r.status === "queued" || r.status === "running").length,
    [runs]
  );

  useEffect(() => {
    if (!pendingCount) return undefined;
    const timer = setInterval(loadRuns, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [pendingCount, loadRuns]);

  // Auto-track only the single latest completed run until the user picks runs
  // manually, so exactly one run is plotted by default on opening this screen.
  useEffect(() => {
    if (!autoTrackRef.current) return;
    const latest = runs
      .filter((r) => r?.status === "completed")
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))[0];
    if (latest) setSelectedRunIds([latest.run_id]);
  }, [runs]);

  // Fetch series for selected runs that are not loaded yet.
  useEffect(() => {
    const missing = selectedRunIds.filter(
      (runId) => !seriesByRun[runId] && !seriesLoadingIds.includes(runId)
    );
    if (!missing.length || !projectId) return;
    setSeriesLoadingIds((prev) => [...prev, ...missing]);
    missing.forEach(async (runId) => {
      try {
        const payload = await fetchWindGenerationRunSeries({ projectId, runId }, accessToken);
        setSeriesByRun((prev) => ({ ...prev, [runId]: payload?.series || [] }));
      } catch {
        setSeriesByRun((prev) => ({ ...prev, [runId]: [] }));
      } finally {
        setSeriesLoadingIds((prev) => prev.filter((id) => id !== runId));
      }
    });
  }, [selectedRunIds, seriesByRun, seriesLoadingIds, projectId, accessToken]);

  const handleTrigger = async () => {
    if (!projectId || !triggerRange?.start || !triggerRange?.end) {
      setTriggerError("Please select a date interval first.");
      return;
    }
    setTriggering(true);
    setTriggerError("");
    try {
      await triggerWindGenerationRun(
        {
          projectId,
          start_date: triggerRange.start,
          end_date: triggerRange.end,
          tz: timeZone,
          runType,
        },
        accessToken
      );
      await loadRuns();
    } catch (err) {
      setTriggerError(formatApiError(err, "Failed to trigger the wind generation run."));
    } finally {
      setTriggering(false);
    }
  };

  // Historical stays a single baseline run; forecast runs can pile up (PV parity).
  const handleToggleRun = useCallback(
    (runId) => {
      const type = runs.find((r) => r.run_id === runId)?.run_type;
      autoTrackRef.current = false;
      setSelectedRunIds((prev) => {
        if (prev.includes(runId)) return prev.filter((id) => id !== runId);
        if (type === "actual_weather") {
          const kept = prev.filter((id) => runs.find((r) => r.run_id === id)?.run_type !== type);
          return [...kept, runId];
        }
        return [...prev, runId];
      });
    },
    [runs]
  );

  const handleRerun = useCallback(
    async (run) => {
      if (!projectId || !run?.start_date || !run?.end_date) return;
      await triggerWindGenerationRun(
        {
          projectId,
          start_date: run.start_date,
          end_date: run.end_date,
          tz: timeZone,
          runType: run.run_type,
        },
        accessToken
      );
      await loadRuns();
    },
    [projectId, timeZone, accessToken, loadRuns]
  );

  const getRunSeries = useCallback(
    async (run) => {
      if (seriesByRun[run.run_id]) return seriesByRun[run.run_id];
      const payload = await fetchWindGenerationRunSeries(
        { projectId, runId: run.run_id },
        accessToken
      );
      const series = payload?.series || [];
      setSeriesByRun((prev) => ({ ...prev, [run.run_id]: series }));
      return series;
    },
    [seriesByRun, projectId, accessToken]
  );

  const handleExportRun = useCallback(
    async (run) => {
      if (!run) return;
      const series = await getRunSeries(run);
      const csv = buildCsv(series, [
        { label: "Time", value: (p) => p.time },
        {
          label: "Generation (kW)",
          value: (p) => (p.generation_kw != null ? Number(p.generation_kw).toFixed(2) : ""),
        },
      ]);
      const type = run.run_type === "actual_weather" ? "historical" : "forecast";
      downloadBlob(
        new Blob([csv], { type: "text/csv;charset=utf-8" }),
        `wind-generation-${type}-${run.start_date}_${run.end_date}.csv`
      );
    },
    [getRunSeries]
  );

  const historicalRun = useMemo(
    () =>
      runs.find((r) => r.run_type === "actual_weather" && selectedRunIds.includes(r.run_id)) ||
      null,
    [runs, selectedRunIds]
  );
  const forecastRuns = useMemo(
    () => runs.filter((r) => r.run_type === "forecast" && selectedRunIds.includes(r.run_id)),
    [runs, selectedRunIds]
  );

  const historicalSeries = historicalRun ? seriesByRun[historicalRun.run_id] : null;
  const forecastEntries = useMemo(
    () => forecastRuns.map((run) => ({ run, series: seriesByRun[run.run_id] || null })),
    [forecastRuns, seriesByRun]
  );

  const historicalMwh = useMemo(
    () => (historicalSeries ? computeSeriesEnergyMwh(historicalSeries) : null),
    [historicalSeries]
  );
  const historicalPeakKw = useMemo(
    () => (historicalSeries ? seriesPeakKw(historicalSeries) : null),
    [historicalSeries]
  );

  const forecastRows = useMemo(
    () =>
      forecastEntries.map(({ run, series }) => {
        const forecastMwh = series ? computeSeriesEnergyMwh(series) : null;
        const differenceMwh =
          historicalMwh != null && forecastMwh != null ? forecastMwh - historicalMwh : null;
        const errorPct =
          differenceMwh != null && historicalMwh ? (differenceMwh / historicalMwh) * 100 : null;
        const forecastPeakKw = series ? seriesPeakKw(series) : null;
        const peakDifferenceKw =
          historicalPeakKw != null && forecastPeakKw != null
            ? forecastPeakKw - historicalPeakKw
            : null;
        const maxDifferenceKw = seriesMaxDifferenceKw(series, historicalSeries);
        return { run, forecastMwh, differenceMwh, errorPct, peakDifferenceKw, maxDifferenceKw, accuracy: null };
      }),
    [forecastEntries, historicalMwh, historicalPeakKw, historicalSeries]
  );

  const handleExportComparisonCsv = useCallback(() => {
    const columns = [
      { label: "Run", value: (r) => r.label },
      { label: "Interval", value: (r) => r.interval },
      { label: "Energy (MWh)", value: (r) => (r.mwh != null ? r.mwh.toFixed(2) : "") },
      { label: "Peak (kW)", value: (r) => (r.peak != null ? r.peak.toFixed(1) : "") },
      { label: "Difference vs historical (MWh)", value: (r) => (r.diff != null ? r.diff.toFixed(2) : "") },
      { label: "Difference vs historical (%)", value: (r) => (r.pct != null ? r.pct.toFixed(1) : "") },
    ];
    const rows = [];
    if (historicalRun) {
      rows.push({
        label: `Historical · ${formatRunTimestamp(historicalRun.created_at, timeZone)}`,
        interval: `${historicalRun.start_date} - ${historicalRun.end_date}`,
        mwh: historicalMwh,
        peak: historicalPeakKw,
        diff: null,
        pct: null,
      });
    }
    forecastRows.forEach((row) => {
      rows.push({
        label: `Forecast · ${formatRunTimestamp(row.run.created_at, timeZone)}`,
        interval: `${row.run.start_date} - ${row.run.end_date}`,
        mwh: row.forecastMwh,
        peak: row.run ? seriesPeakKw(seriesByRun[row.run.run_id]) : null,
        diff: row.differenceMwh,
        pct: row.errorPct,
      });
    });
    const csv = buildCsv(rows, columns);
    downloadBlob(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
      `wind-generation-comparison-${Date.now()}.csv`
    );
  }, [historicalRun, historicalMwh, historicalPeakKw, forecastRows, seriesByRun, timeZone]);

  const handleChangeRun = () => {
    historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Stack spacing={3}>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Wind Generation Estimate
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Estimate and compare wind farm production using forecast or historical weather
              at the configured wind farm location.
            </Typography>
          </Stack>

          {!windEnabled && (
            <Alert severity="info">
              Wind generation is not enabled for this project. Enable it in the Project page
              and configure the farm in the Wind Farm page.
            </Alert>
          )}

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", md: "center" }}
            flexWrap="wrap"
            sx={{ rowGap: 1 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Run on:
            </Typography>
            <Stack direction="row" spacing={1}>
              {RUN_TYPE_OPTIONS.map((opt) => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  color={runType === opt.key ? "primary" : "default"}
                  variant={runType === opt.key ? "filled" : "outlined"}
                  onClick={() => onRunTypeChange(opt.key)}
                  sx={{ fontWeight: 700 }}
                />
              ))}
            </Stack>
            <Button
              variant="outlined"
              size="small"
              onClick={onOpenRangePicker}
              startIcon={<CalendarMonthIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              endIcon={<ArrowDropDownIcon fontSize="small" sx={{ color: "text.secondary" }} />}
              sx={{ minWidth: 180, textTransform: "none" }}
            >
              {rangeLabelNode}
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={
                triggering ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon fontSize="small" />
              }
              disabled={triggering || !projectId || !windEnabled}
              onClick={handleTrigger}
              sx={{ textTransform: "none", fontWeight: 700 }}
            >
              Estimate Generation
            </Button>
          </Stack>

          {!projectId && (
            <Alert severity="info">Select a project to trigger and review wind generation runs.</Alert>
          )}
          {triggerError && <Alert severity="error">{triggerError}</Alert>}
          {pendingCount > 0 && (
            <Alert severity="info" icon={<CircularProgress size={18} />}>
              Running...
            </Alert>
          )}
          {runsError && <Alert severity="error">{runsError}</Alert>}
          {!runsError && !runsLoading && runs.length === 0 && (
            <Alert severity="info">
              Wind generation data not available yet. Select a date range and run a wind
              generation estimation.
            </Alert>
          )}
        </Stack>

        {(runsLoading || runs.length > 0) && !runsError && (
          <>
            <Divider sx={{ my: 2 }} />
            {runsLoading && runs.length === 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} /> Loading runs...
              </Box>
            ) : (
              <Stack spacing={2}>
                <PvComparisonSummary
                  historicalRun={historicalRun}
                  forecastRuns={forecastRuns}
                  onChangeRun={handleChangeRun}
                  onRemoveForecastRun={handleToggleRun}
                  measuredAvailable={false}
                  measuredLoading={false}
                  measuredError=""
                  timeZone={timeZone}
                  showMeasured
                />

                <PvKpiCards
                  historicalMwh={historicalMwh}
                  forecastRows={forecastRows}
                  measuredAvailable={false}
                  timeZone={timeZone}
                />

                <Box>
                  <PvGenerationChart
                    historicalRun={historicalRun}
                    forecastEntries={forecastEntries}
                    historicalSeries={historicalSeries}
                    measuredSeries={null}
                    measuredAvailable={false}
                    historicalMwh={historicalMwh}
                    forecastRows={forecastRows}
                    timeZone={timeZone}
                    onExportComparisonExcel={handleExportComparisonCsv}
                    comparisonExportLabel="Current comparison to CSV"
                    onExportRun={handleExportRun}
                    exportBaseName="wind-generation"
                    onFullscreen={() => setChartFullscreen(true)}
                  />
                </Box>

                <Box ref={historyRef}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 800, color: "text.secondary", letterSpacing: "0.04em", mb: 1 }}
                  >
                    RUN HISTORY
                  </Typography>
                  <WindRunHistoryTable
                    runs={runs}
                    selectedRunIds={selectedRunIds}
                    onToggleRun={handleToggleRun}
                    seriesLoadingIds={seriesLoadingIds}
                    onExportRun={handleExportRun}
                    onRerun={handleRerun}
                    timeZone={timeZone}
                  />
                </Box>
              </Stack>
            )}
          </>
        )}
      </Paper>

      <Dialog fullScreen open={chartFullscreen} onClose={() => setChartFullscreen(false)}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <IconButton onClick={() => setChartFullscreen(false)} title="Exit full screen">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ height: "100%", pt: 0 }}>
          <PvGenerationChart
            variant="fullscreen"
            historicalRun={historicalRun}
            forecastEntries={forecastEntries}
            historicalSeries={historicalSeries}
            measuredSeries={null}
            measuredAvailable={false}
            historicalMwh={historicalMwh}
            forecastRows={forecastRows}
            timeZone={timeZone}
            onExportComparisonExcel={handleExportComparisonCsv}
            comparisonExportLabel="Current comparison to CSV"
            onExportRun={handleExportRun}
            exportBaseName="wind-generation"
          />
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
